import { SITE_URL } from "./site";

// A public measurement identifier, not a secret or a Measurement Protocol key.
export const ANALYTICS_ID = "G-YEWY5QBDNR";
export const ANALYTICS_CONSENT_KEY = "kaizen:analytics-consent";
export const ANALYTICS_CONSENT_VERSION = 1;
export const ANALYTICS_LIFETIME_SECONDS = 180 * 24 * 60 * 60;

export type AnalyticsConsent = {
  version: number;
  choice: "granted" | "denied";
  expiresAt: number;
};

export function readAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const value = JSON.parse(localStorage.getItem(ANALYTICS_CONSENT_KEY) || "null");
    if (
      value?.version === ANALYTICS_CONSENT_VERSION &&
      ["granted", "denied"].includes(value.choice) &&
      typeof value.expiresAt === "number" &&
      value.expiresAt > Date.now()
    ) return value;
  } catch { /* Unavailable storage means ask again, never assume consent. */ }
  return null;
}

export function clearAnalyticsCookies() {
  const labels = location.hostname.split(".");
  const domains = ["", ...labels.map((_, index) => `; Domain=${labels.slice(index).join(".")}`)];
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.trim().split("=")[0];
    if (name !== "_ga" && !name.startsWith("_ga_")) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax`;
    }
  }
}

export function analyticsReferrer(value: string) {
  try {
    const url = new URL(value);
    // Only the referring origin: never forward paths, email addresses or tokens.
    return ["https:", "http:"].includes(url.protocol) ? url.origin : "";
  } catch { return ""; }
}

export type AnalyticsPage = { path: string; title: string; referrer: string };

export function analyticsFrameDocument(page: AnalyticsPage) {
  const data = JSON.stringify({
    id: ANALYTICS_ID,
    lifetime: ANALYTICS_LIFETIME_SECONDS,
    page: {
      page_location: `${SITE_URL}${page.path}`,
      page_title: page.title,
      page_referrer: page.referrer,
    },
  }).replaceAll("<", "\\u003c");

  // A disposable, same-origin document keeps Google's automatic DOM/history
  // listeners away from the authentication and workspace screens on `/`.
  // Public navigation recreates it; leaving public content destroys it.
  return `<!doctype html><html><head><meta charset="utf-8">
<meta name="referrer" content="no-referrer"><title>Kaizen usage analytics</title>
<script>
(function() {
// A direct visit is never a tracking page. A frame also needs current consent.
if (parent === window) return;
if (location.protocol !== 'https:' || !['kaizen.co.ke', 'www.kaizen.co.ke'].includes(location.hostname)) return;
try {
  const consent = JSON.parse(localStorage.getItem('${ANALYTICS_CONSENT_KEY}') || 'null');
  if (consent?.version !== ${ANALYTICS_CONSENT_VERSION} || consent.choice !== 'granted' ||
      typeof consent.expiresAt !== 'number' || consent.expiresAt <= Date.now()) return;
} catch { return; }
const settings = ${data};
window.dataLayer = [];
function gtag(){ dataLayer.push(arguments); }
window.gtag = gtag;
gtag('consent', 'default', {
  analytics_storage: 'denied', ad_storage: 'denied',
  ad_user_data: 'denied', ad_personalization: 'denied'
});
gtag('consent', 'update', { analytics_storage: 'granted' });
gtag('js', new Date());
gtag('set', settings.page);
gtag('config', settings.id, {
  ...settings.page, send_page_view: false,
  allow_google_signals: false, allow_ad_personalization_signals: false,
  cookie_expires: settings.lifetime, cookie_update: false,
  cookie_flags: 'SameSite=Lax;Secure'
});
gtag('event', 'page_view', { ...settings.page, send_to: settings.id });
let scrolled = false;
addEventListener('message', function(event) {
  if (event.source !== parent || event.origin !== location.origin ||
      event.data !== 'kaizen:analytics:scroll' || scrolled ||
      window['ga-disable-' + settings.id]) return;
  scrolled = true;
  gtag('event', 'scroll', { ...settings.page, send_to: settings.id, percent_scrolled: 90 });
});
const script = document.createElement('script');
script.async = true;
script.referrerPolicy = 'no-referrer';
script.src = 'https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_ID}';
document.head.appendChild(script);
})();
</script>
</head><body></body></html>`;
}
